import { useState } from 'react';

import {
  Box,
  Card,
  Divider,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

import { Button } from '@karehero/llama/components/atoms/Button/Button';
import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

import BottomSheet from '@/designs-dev/device/BottomSheet';
import UploadDocumentButton from '@/karehero-app/shell/UploadDocumentButton';

const ASSIGNEES = ['You', 'Yasmin', 'Abbie'];
const UNITS = ['Days', 'Weeks', 'Months'];

// Create-action form sheet. Toggling Repeats expands the recurrence fields and
// turns Date into Starts.
const CreateAction = ({
  onClose,
  initialRepeats = false,
}: {
  onClose: () => void;
  /** Jump-state for handover minis: open with the repeat schedule expanded. */
  initialRepeats?: boolean;
}) => {
  const [repeats, setRepeats] = useState(initialRepeats);

  const footer = (
    <Button
      variant='primary'
      size='lg'
      isFullWidth
      ariaLabel='Create action'
      onPress={onClose}
    >
      Create action
    </Button>
  );

  return (
    <BottomSheet
      title='Create action'
      titleAlign='center'
      onClose={onClose}
      footer={footer}
    >
      <Stack sx={{ pt: 0.5, pb: 2 }} spacing={2}>
        <TextField
          slotProps={{ inputLabel: { shrink: true } }}
          label='Title'
          fullWidth
        />
        {/* Under the title, because it is the rest of what the action is:
            the title names it and this says the bit that would not fit. A CAM
            sets actions with a note on them, and a member making one has the
            same thing to say. */}
        <TextField
          slotProps={{ inputLabel: { shrink: true } }}
          label='Note'
          multiline
          minRows={3}
          fullWidth
        />

        <TextField
          slotProps={{
            inputLabel: { shrink: true },
            select: {
              displayEmpty: true,
              renderValue: (v) =>
                v ? String(v) : 'Who should complete this task?',
            },
          }}
          select
          fullWidth
          label='Assignee'
          defaultValue=''
        >
          {ASSIGNEES.map((a) => (
            <MenuItem key={a} value={a}>
              {a}
            </MenuItem>
          ))}
        </TextField>

        {/* repeats toggle (expands when on) */}
        <Card sx={{ p: 2 }}>
          <Stack
            sx={{ alignItems: 'center', justifyContent: 'space-between' }}
            direction='row'
            spacing={1}
          >
            <Stack spacing={0.25}>
              <Typography variant='subtitle1' sx={{ color: 'text.primary' }}>
                Repeats
              </Typography>
              <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                Schedule a recurring action
              </Typography>
            </Stack>
            <Switch
              color='primary'
              checked={repeats}
              onChange={(e) => setRepeats(e.target.checked)}
            />
          </Stack>
          {repeats && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography
                variant='overline'
                sx={{ display: 'block', mb: 1, color: 'text.secondary' }}
              >
                EVERY
              </Typography>
              <Stack direction='row' spacing={1.5}>
                <TextField
                  slotProps={{ inputLabel: { shrink: true } }}
                  defaultValue='2'
                  sx={{ width: 88 }}
                />
                <TextField select fullWidth defaultValue='Weeks'>
                  {UNITS.map((u) => (
                    <MenuItem key={u} value={u}>
                      {u}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>
            </>
          )}
        </Card>

        <TextField
          slotProps={{ inputLabel: { shrink: true } }}
          label={repeats ? 'Starts' : 'Date'}
          fullWidth
        />
        <TextField
          slotProps={{ inputLabel: { shrink: true } }}
          label='Time'
          fullWidth
        />

        {/* uploads */}
        <Box>
          <Typography
            variant='overline'
            color='text.secondary'
            sx={{ display: 'block', mb: 1 }}
          >
            UPLOADS
          </Typography>
          <UploadDocumentButton />
        </Box>
      </Stack>
    </BottomSheet>
  );
};

export default CreateAction;
